import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Download, CheckCircle2, Loader2 } from "lucide-react";
import publicApi from "../../api/public";

export default function NewsletterCta() {
  const [email, setEmail] = useState("");
  const [nom, setNom] = useState("");
  const [tel, setTel] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      await publicApi.post("/contact", {
        nom: nom || "Demande brochure",
        prenom: "",
        telephone: tel,
        email,
        message: "Demande de réception de la brochure TPCO Sénégal (catalogue de projets et tarifs).",
        type: "contact",
      });
      setSent(true);
    } catch (_e) {
      setErr("Erreur d'envoi — réessaye ou contacte-nous directement.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-gradient-to-br from-[#0F1A3D] via-[#1E3A8A] to-[#0F1A3D] py-16 lg:py-20 overflow-hidden relative" data-testid="newsletter-cta">
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-1/3 h-1 bg-[#E91E63]" />
      <div className="absolute bottom-0 right-0 w-1/3 h-1 bg-[#E91E63]" />

      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Texte */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E91E63]/15 text-[#E91E63] rounded-full text-[10px] uppercase tracking-[0.25em] font-bold mb-5">
              <Download className="w-3.5 h-3.5" /> Téléchargement gratuit
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Recevez notre <span className="text-[#E91E63]">brochure complète</span> 2026
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed text-[15px]">
              48 pages détaillant tous nos programmes, plans, prix au m², modalités de financement et témoignages clients. PDF envoyé immédiatement.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/80">
              {[
                "Catalogue complet des projets",
                "Grille tarifaire transparente",
                "Plans et fiches techniques",
                "Modalités de financement",
              ].map((it) => (
                <li key={it} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#E91E63] shrink-0" /> {it}</li>
              ))}
            </ul>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-2xl p-6 lg:p-8 shadow-2xl"
          >
            {sent ? (
              <div className="text-center py-8" data-testid="brochure-sent">
                <div className="w-16 h-16 rounded-full bg-[#E5F8E9] text-[#2D7A3F] flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-xl font-bold text-[#0F1A3D] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Demande envoyée</h3>
                <p className="text-sm text-[#4B5775]">Notre équipe vous envoie la brochure sous 24h. Merci !</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="brochure-form">
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#E91E63] font-bold flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Demande de brochure
                </div>
                <input
                  required
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  placeholder="Votre nom complet *"
                  className="w-full h-12 px-4 text-sm rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
                  data-testid="brochure-nom"
                />
                <input
                  required
                  type="tel"
                  value={tel}
                  onChange={(e) => setTel(e.target.value)}
                  placeholder="Téléphone * (+221 77…)"
                  className="w-full h-12 px-4 text-sm rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
                  data-testid="brochure-tel"
                />
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email *"
                  className="w-full h-12 px-4 text-sm rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
                  data-testid="brochure-email"
                />
                {err && <div className="text-xs text-[#C83B3B] bg-[#FCE4EC] p-3 rounded">{err}</div>}
                <button type="submit" disabled={sending} className="w-full h-12 rounded-lg bg-[#E91E63] hover:bg-[#D81B60] text-white font-bold text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2 transition disabled:opacity-50" data-testid="brochure-submit">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> Recevoir la brochure</>}
                </button>
                <p className="text-[10px] text-[#4B5775] text-center pt-1">Vos données restent confidentielles · RGPD</p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
